import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, Coins, ArrowRight } from "lucide-react";

export default function CreditsSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("loading");
  const [creditsAdded, setCreditsAdded] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId && user) {
      checkPurchaseStatus();
    }
  }, [sessionId, user]);

  const checkPurchaseStatus = async () => {
    try {
      const response = await axios.get(`${API}/credits/checkout-status/${sessionId}`);
      
      if (response.data.status === "complete" && response.data.payment_status === "paid") {
        setStatus("success");
        setCreditsAdded(response.data.credits_added);
      } else {
        setStatus("pending");
        // Retry after 2 seconds
        setTimeout(checkPurchaseStatus, 2000);
      }
    } catch (error) {
      console.error("Failed to check status", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen py-16 flex items-center justify-center" data-testid="credits-success-page">
      <Card className="max-w-md w-full mx-4">
        <CardHeader className="text-center">
          {status === "loading" || status === "pending" ? (
            <>
              <div className="mx-auto w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="h-8 w-8 text-gold animate-spin" />
              </div>
              <CardTitle className="font-serif text-2xl">Processing Purchase...</CardTitle>
            </>
          ) : status === "success" ? (
            <>
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <CardTitle className="font-serif text-2xl">Credits Added!</CardTitle>
            </>
          ) : (
            <>
              <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <Coins className="h-8 w-8 text-red-500" />
              </div>
              <CardTitle className="font-serif text-2xl">Something Went Wrong</CardTitle>
            </>
          )}
        </CardHeader>

        <CardContent className="text-center space-y-6">
          {status === "loading" || status === "pending" ? (
            <p className="text-muted-foreground">
              Please wait while we confirm your purchase...
            </p>
          ) : status === "success" ? (
            <>
              <p className="text-muted-foreground">
                Your credits have been added to your account.
              </p>
              
              {creditsAdded && (
                <div className="bg-card border border-border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Audio Credits</span>
                    <span className="font-bold text-gold">+{creditsAdded.audio}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">PDF Credits</span>
                    <span className="font-bold text-gold">+{creditsAdded.pdf}</span>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col gap-3">
                <Button 
                  className="w-full bg-gold text-primary-foreground hover:bg-gold/90"
                  onClick={() => navigate("/library")}
                >
                  Start Learning
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/account")}
                >
                  View Account
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted-foreground">
                There was an issue processing your purchase. Please contact support if you were charged.
              </p>
              <Button 
                variant="outline"
                onClick={() => navigate("/account")}
              >
                Go to Account
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
